import * as XLSX from 'xlsx';

/**
 * Parse an Excel or CSV file and return the data as an array of objects
 * @param file The file to parse
 * @returns Promise that resolves to an array of objects representing the data
 */
export const parseExcelFile = (file: File): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        if (!data) {
          reject(new Error('Failed to read file'));
          return;
        }
        
        const workbook = XLSX.read(data, { type: 'binary' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to JSON with headers
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 'A' }) as Record<string, any>[];
        
        if (jsonData.length === 0) {
          reject(new Error('No data found in file'));
          return;
        }
        
        // Extract headers from the first row
        const firstRow = jsonData[0] as Record<string, any>;
        const headers = Object.keys(firstRow).reduce((acc: Record<string, string>, key) => {
          acc[key] = String(firstRow[key]);
          return acc;
        }, {});
        
        // Map data to objects with proper headers
        const rows = jsonData.slice(1).map((row: Record<string, any>) => {
          const mappedRow: Record<string, any> = {};
          Object.keys(row).forEach((key) => {
            const header = headers[key];
            mappedRow[header] = row[key];
          });
          return mappedRow;
        });
        
        // Add mock IDs for DataGrid
        const rowsWithIds = rows.map((row: any, index: number) => ({
          id: index + 1,
          ...row
        }));
        
        // Add mock classification data for demo purposes
        const mockCategories = [
          'Office Supplies', 
          'IT Equipment', 
          'Professional Services', 
          'Travel', 
          'Facilities', 
          'Marketing'
        ];
        
        const mockSubcategories: Record<string, string[]> = {
          'Office Supplies': ['Paper', 'Pens', 'Furniture', 'Stationery'],
          'IT Equipment': ['Hardware', 'Software', 'Services', 'Accessories'],
          'Professional Services': ['Consulting', 'Legal', 'Accounting', 'Training'],
          'Travel': ['Airfare', 'Accommodation', 'Transportation', 'Meals'],
          'Facilities': ['Rent', 'Utilities', 'Maintenance', 'Security'],
          'Marketing': ['Advertising', 'Events', 'Promotions', 'Materials']
        };
        
        const rowsWithClassification = rowsWithIds.map((row: any) => {
          const randomCategoryIndex = Math.floor(Math.random() * mockCategories.length);
          const category = mockCategories[randomCategoryIndex];
          const subcategories = mockSubcategories[category];
          const randomSubcategoryIndex = Math.floor(Math.random() * subcategories.length);
          const subcategory = subcategories[randomSubcategoryIndex];
          const confidence = Math.floor(Math.random() * 40) + 60; // Random confidence between 60-100
          
          return {
            ...row,
            category,
            subcategory,
            confidence
          };
        });
        
        resolve(rowsWithClassification);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Error reading file'));
    };
    
    reader.readAsBinaryString(file);
  });
}; 